import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { GET_ALL_ROLES, DELETE_ROLE } from '../../graphql/queries';
import { Spinner } from '../common/Spinner';
import AssignRoleModal from './AssignRoleModal';

interface Role {
  roleId: string;
  roleName: string;
  displayName?: string;
  description?: string | null;
  permissions: string[];
  isSystemRole: boolean;
  isActive: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const formatDate = (iso?: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '-';
  return d.toISOString().slice(0, 10);
};

const roleBadgeClass = (roleName: string) => {
  const n = roleName.toLowerCase();
  if (n === 'admin') return 'bg-red-100 text-red-700';
  if (n === 'agency') return 'bg-teal-100 text-teal-700';
  if (n === 'brand' || n === 'host') return 'bg-blue-100 text-blue-700';
  if (n === 'influencer') return 'bg-purple-100 text-purple-700';
  return 'bg-gray-100 text-gray-700';
};

/**
 * 역할 및 권한관리 — lists every role defined in the system (both built-in
 * system roles and custom ones created via the 권한 추가 modal) with their
 * permission summary, letting admins create new roles or delete custom ones.
 */
const UserRolesPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);

  const { data, loading, error, refetch } = useQuery<{ getAllRoles: { roles: Role[] } }>(
    GET_ALL_ROLES,
    {
      variables: { includeInactive: true },
      fetchPolicy: 'network-only',
    },
  );

  const [deleteRole, { loading: deleting }] = useMutation(DELETE_ROLE);

  const roles = data?.getAllRoles?.roles ?? [];
  const total = roles.length;

  // Sort: system roles first, then by name.
  const sorted = useMemo(
    () =>
      [...roles].sort((a, b) => {
        if (a.isSystemRole !== b.isSystemRole) return a.isSystemRole ? -1 : 1;
        return a.roleName.localeCompare(b.roleName);
      }),
    [roles],
  );

  const handleDelete = async (role: Role) => {
    if (role.isSystemRole) {
      toast.error(
        t('admin:userRoles.cannotDeleteSystem', '시스템 역할은 삭제할 수 없습니다'),
      );
      return;
    }
    if (
      !window.confirm(
        t('admin:userRoles.confirmDelete', '{{role}} 역할을 삭제하시겠습니까?', {
          role: role.roleName,
        }),
      )
    ) {
      return;
    }
    try {
      await deleteRole({ variables: { roleId: role.roleId } });
      toast.success(
        t('admin:userRoles.deleteSuccess', '{{role}} 역할이 삭제되었습니다', {
          role: role.roleName,
        }),
      );
      await refetch();
    } catch (err: any) {
      toast.error(err?.message || t('admin:userRoles.deleteFailed', '역할 삭제에 실패했습니다'));
    }
  };

  const formatPermissions = (perms: string[]) => {
    if (!perms || perms.length === 0) return '-';
    if (perms.includes('*:*')) return t('admin:userRoles.permissionAll', '전체 권한');
    if (perms.length <= 4) return perms.join(', ');
    return `${perms.slice(0, 3).join(', ')} +${perms.length - 3}`;
  };

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span>{t('admin:sidebar.accountManagement')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{t('admin:sidebar.accountsAndRoles')}</span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('admin:sidebar.accountsAndRoles')}
          </h1>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4" />
            {t('admin:userRoles.addNewRoleBtn', '역할 추가')}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">
              {t('admin:userRoles.roleListTitle', '역할 리스트')}
            </h2>
            <span className="text-xs text-gray-500">
              {t('admin:userRoles.totalCount', { count: total, defaultValue: '전체 {{count}}개' })}
            </span>
          </div>

          {error && (
            <div className="px-4 py-6 text-sm text-red-600">{error.message}</div>
          )}

          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:userRoles.table.roleName', '역할')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:userRoles.table.description', '설명')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:userRoles.table.permissions', '권한')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:userRoles.table.type', '유형')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:userRoles.table.createdAt', '생성일')}
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  {t('admin:userRoles.table.action', '액션')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <Spinner size="medium" />
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    {t('admin:userRoles.empty', '등록된 역할이 없습니다.')}
                  </td>
                </tr>
              ) : (
                sorted.map((role) => (
                  <tr
                    key={role.roleId}
                    className={`border-t border-gray-100 hover:bg-gray-50 ${role.isActive === false ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${roleBadgeClass(role.roleName)}`}
                      >
                        {role.roleName}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[280px] truncate" title={role.description ?? ''}>
                      {role.description || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="font-mono text-xs">{formatPermissions(role.permissions)}</span>
                      {role.permissions && role.permissions.length > 0 && (
                        <span className="ml-1 text-[11px] text-gray-400">
                          ({role.permissions.length})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${role.isSystemRole ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-700'}`}
                      >
                        {role.isSystemRole
                          ? t('admin:userRoles.type.system', '시스템')
                          : t('admin:userRoles.type.custom', '커스텀')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(role.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setEditRole(role)}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <PencilIcon className="w-4 h-4" />
                          {t('admin:userRoles.table.edit', '권한 수정')}
                        </button>
                        {role.isSystemRole ? (
                          <span className="text-xs text-gray-400">
                            {t('admin:userRoles.table.protected', '보호됨')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDelete(role)}
                            disabled={deleting}
                            className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            <TrashIcon className="w-4 h-4" />
                            {t('common:delete', '삭제')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <AssignRoleModal
          onClose={() => setShowModal(false)}
          onChanged={() => {
            refetch();
          }}
        />
      )}

      {editRole && (
        <AssignRoleModal
          role={{
            roleId: editRole.roleId,
            roleName: editRole.roleName,
            description: editRole.description,
            permissions: editRole.permissions,
            isSystemRole: editRole.isSystemRole,
          }}
          onClose={() => setEditRole(null)}
          onChanged={() => {
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default UserRolesPage;
